import useWindowSize from "@dana/Common/Hooks/useWindowSize";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { SoldToAddress } from "@dana/Types/ApiModels";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getFulfillmentLabel, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {

    const session = state.context.session;
    const { shipToId, billToId, properties } = state.context.session;
    const shipToState = getShipToState(state, shipToId);

    const { value: shipTo } = shipToState;
    const billToState = getBillToState(state, billToId);
    const { value: billTo } = billToState;
   
    const soldTo = session?.properties?.soldToAddress &&
        session?.properties?.soldToAddress !== "" &&
        session?.properties?.soldToAddress !== undefined
        ? (JSON.parse(session?.properties?.soldToAddress) as SoldToAddress)
        : undefined;
    return {
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        session,
        shipTo,
        billTo,
        soldTo

    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickableCustomer?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypographyCustomer?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickableCustomer: {
        css: css`
            margin-left: 10px;
            width: calc(100% - 10px);
            padding: 6px 0;

            &:hover,
            &:focus,
            &:focus-visible {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaBrightBlue};
                    }
                }
                ${TypographyStyle} {
                    color: ${themeColors.secondary.danaBrightBlue};
                }
            }

            &:focus,
            &:focus-visible {
                outline: none;
                border: 1px solid ${themeColors.primary.white};
                box-shadow: ${elevation.focus};
            }

            &:disabled {
                svg {
                    path {
                        fill: ${themeBackgrounds.darkGray};
                    }
                }
                ${TypographyStyle} {
                    color: ${themeBackgrounds.darkGray};
                }
            }
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypographyCustomer: {
        css: css`
            margin-left: 10px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,

    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    session,
    billTo,
    shipTo,
    soldTo
}) => {

    const [userType, setUserType] = useState<string>();
    const [customerNumber, setCustomerNumber] = useState<string>();
    const [customerName, setCustomerName] = useState<string>();

    useEffect(() => {

        let addressLabel = "";
        let nameLabel = "";

        const selId = getCookie("selectBillLa");
        if ((session?.isAuthenticated || session?.rememberMe) && !session?.isGuest && shipTo && soldTo) {
            if (selId === shipTo.id.toString()) {
                addressLabel = shipTo.customerSequence;
                nameLabel = `${shipTo.firstName ?? ""}`.trim();
            }
            else if (selId === billTo?.id.toString()) {
                addressLabel = billTo?.customerSequence ?? '';
                nameLabel = `${billTo?.firstName ?? ""}`.trim();
            }
            else if (selId === soldTo?.Id) {
                addressLabel = soldTo?.CustomerSequence;
                nameLabel = `${soldTo?.FirstName ?? ""}`.trim();
            }
            else {
                addressLabel = soldTo?.CustomerSequence;
                nameLabel = `${soldTo?.FirstName ?? ""} ${soldTo?.LastName ?? ""}`.trim();
            }
            setCustomerNumber(addressLabel);
            setCustomerName(nameLabel || "");
        }

        setUserType(session?.properties?.userType)
    }, [session, billTo, shipTo, soldTo]);

    const [isCustomerButtonClicked, setIsCustomerButtonClicked] = React.useState(false);

    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
        setIsCustomerButtonClicked(true);
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
        setIsCustomerButtonClicked(false);
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    const { width } = useWindowSize();
    return (
        <>
            {userType !== "ERFQ" && (
                <>
                    <StyledWrapper {...styles.shipToAddressWrapper}>
                        <Clickable
                            {...styles.titleClickableCustomer}
                            onClick={handleClickAddress}
                            className={`customer-number-button ${isCustomerButtonClicked ? "clicked" : ""}`}
                            data-test-selector="header_shipToButton"
                        >
                            <Typography {...styles.titleTypographyCustomer} data-test-selector="header_shipToLabel">
                                {customerNumber ? `${translate("Customer No.")} ${customerNumber} ${customerName ? ` - ${customerName}` : ""}` : translate("Select Customer")}
                            </Typography>
                        </Clickable>
                    </StyledWrapper>
                    {width !== null && width > 992 && (
                        <Drawer
                            {...drawerStyles}
                            position="top"
                            isOpen={isDrawerOpen}
                            headline={isPunchOutSession ? "" : translate("Change Customer")}
                            handleClose={handleCloseDrawer}
                            data-test-selector="addressDrawer"
                        >
                            <Zone zoneName="Container" contentId={id} />
                        </Drawer>
                    )}
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
